import { useState, useCallback, useEffect } from 'react';
import { State as ODataState } from '@progress/kendo-data-query';
import { Grid, GridColumn, GridDataStateChangeEvent, GridNoRecords, GridCustomFilterCellProps, GridCellProps } from '@progress/kendo-react-grid';
import { HeaderThElement } from '@progress/kendo-react-data-tools';
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import { Card, CircularProgress } from '@mui/material';
import PageContainerLayout from 'src/pages/PageContainerLayout';
import AdminApi from 'src/api/AdminApi';
import { CHRobinsonLog } from 'src/types/chRobinsonLog';
import { userHasRole } from 'src/utils/userHelpers';
import DateTimeCell from 'src/components/grid/DateTimeCell';
import ClipboardCell from 'src/components/grid/ClipboardCell';

const dateTimeCell = (cellProps: GridCellProps) => <DateTimeCell {...cellProps} />;
const clipboardCell = (cellProps) => <ClipboardCell {...cellProps} />;

const TextFilterCell = (props: GridCustomFilterCellProps) => {
  const handleChange = (e: InputChangeEvent) => {
    props.onChange({
      value: e.value,
      operator: 'contains',
      syntheticEvent: e.syntheticEvent,
    });
  };

  return (
    <HeaderThElement columnId={props.thProps?.columnId || ''} {...props.thProps}>
      <Input value={props.value || ''} onChange={handleChange} />
    </HeaderThElement>
  );
};

const CHRobinson = () => {
  const isSalesManager = userHasRole(['Sales Manager']);
  const [data, setData] = useState<CHRobinsonLog[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [gridDataState, setGridDataState] = useState<ODataState>({
    take: 20,
    skip: 0,
    sort: [{ field: 'createdDate', dir: 'desc' }],
  });

  const loadData = useCallback((dataState: ODataState) => {
    setLoading(true);
    AdminApi.getCHRobinsonLogs(dataState)
      .then((response) => {
        setData(response.value);
        setTotal(response['@odata.count'] || 0);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        console.error('Failed to fetch CH Robinson logs');
      });
  }, []);

  useEffect(() => {
    if (isSalesManager) {
      loadData(gridDataState);
    }
  }, [isSalesManager]);

  const handleDataStateChange = (event: GridDataStateChangeEvent) => {
    setGridDataState(event.dataState);
    loadData(event.dataState);
  };

  if (!isSalesManager) {
    return (
      <PageContainerLayout pageHeaderTitle="CH Robinson">
        <p>You do not have permission to view this page. Sales Manager access required.</p>
      </PageContainerLayout>
    );
  }

  return (
    <div className="chrobinson-page">
      <PageContainerLayout pageHeaderTitle="CH Robinson">
        <Card>
          <Grid
            data={data}
            total={total}
            pageable={{ pageSizes: [20, 50, 200] }}
            sortable
            filterable
            scrollable="scrollable"
            {...gridDataState}
            onDataStateChange={handleDataStateChange}
            style={{ width: '100%', maxHeight: '700px' }}
            dataItemKey="referenceNumber"
          >
            <GridColumn field="referenceNumber" title="Reference Number" width={320} cells={{ filterCell: TextFilterCell }} />
            <GridColumn field="createdDate" title="Created Date" width={250} filter="date" cells={{ data: dateTimeCell }} />
            <GridColumn field="bolNumber" title="BOL Number" width={130} cells={{ filterCell: TextFilterCell }} />
            <GridColumn field="customerNumber" title="Customer #" width={130} cells={{ filterCell: TextFilterCell }} />
            <GridColumn field="orderNumbers" title="Order Numbers" width={160} cells={{ filterCell: TextFilterCell }} />
            <GridColumn field="shipToName" title="Ship To Name" width={180} cells={{ filterCell: TextFilterCell }} />
            <GridColumn field="filePath" title="File Path" width={270} cells={{ data: clipboardCell, filterCell: TextFilterCell }} />
            <GridColumn field="error" title="Error" width={250} cells={{ data: clipboardCell, filterCell: TextFilterCell }} />
            <GridColumn field="request" title="Request" width={250} cells={{ data: clipboardCell, filterCell: TextFilterCell }} />
            <GridColumn field="response" title="Response" width={250} cells={{ data: clipboardCell, filterCell: TextFilterCell }} />
            <GridNoRecords>{loading ? <CircularProgress /> : 'No CH Robinson logs found.'}</GridNoRecords>
          </Grid>
        </Card>
      </PageContainerLayout>
    </div>
  );
};

export default CHRobinson;
